/**
 * Non-blocking notices — saved, copied, connected.
 *
 * A toast is for something the reader does not have to act on. A dependency
 * failure the reader must act on is a persistent inline `<Alert>`, never a
 * toast that disappears while they are reading it.
 *
 * **THE STACK SITS AT THE TOP OF THE VIEWPORT, CENTRED.** It used to sit in the
 * bottom inline-start corner. A reader who presses a control halfway down a long
 * settings page is looking at that control, and the answer appeared outside the
 * region they were reading; centred at the top, one position serves every page
 * whatever its length. It is the same position the operator console and the
 * public site now use — three surfaces, one place to look.
 *
 * **The chrome is `<Alert>`'s, deliberately.** Same four kinds, same soft fill,
 * same glyph, so «saved» looks like «saved» whether it is drawn inline or
 * floating. The glyph is what separates the kinds — a coloured edge alone would
 * identify them by hue only (WCAG 1.4.1).
 *
 * **The whole notice is one button, and pressing it dismisses it.** It carries no
 * decision — a decision is a dialog — so one tab stop is the whole of it, and it
 * is the same shape the operator console draws.
 */
import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react';

import { cn } from '../cn';

export type ToastIntent = 'success' | 'info' | 'warning' | 'danger';

interface Toast {
  id: number;
  intent: ToastIntent;
  message: string;
}

interface ToastValue {
  toast: (message: string, intent?: ToastIntent) => void;
  dismiss: (id: number) => void;
}

const ToastContext = createContext<ToastValue | null>(null);

const DISMISS_AFTER_MS = 6000;

const INTENT_CLASS: Record<ToastIntent, string> = {
  success: 'border-success bg-success-soft',
  info: 'border-info bg-info-soft',
  warning: 'border-warning bg-warning-soft',
  danger: 'border-danger bg-danger-soft',
};

/** The same four marks `<Alert>` draws, so one notice reads one way. */
const INTENT_GLYPH: Record<ToastIntent, string> = {
  success: '✓',
  info: 'ℹ',
  warning: '⚠',
  danger: '✕',
};

let nextId = 0;

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);

  const dismiss = useCallback((id: number) => {
    setToasts((current) => current.filter((entry) => entry.id !== id));
  }, []);

  const toast = useCallback(
    (message: string, intent: ToastIntent = 'info') => {
      const id = (nextId += 1);
      setToasts((current) => [...current, { id, intent, message }]);
      window.setTimeout(() => dismiss(id), DISMISS_AFTER_MS);
    },
    [dismiss],
  );

  const value = useMemo<ToastValue>(() => ({ toast, dismiss }), [toast, dismiss]);

  return (
    <ToastContext.Provider value={value}>
      {children}
      {/* `start-0 end-0` with `items-center` centres the stack without a
          `translate`, which would re-scope `position: fixed` for anything
          inside it. The band itself takes no pointer events, so it never
          covers the header's controls; each toast takes them back. */}
      <div
        aria-live="polite"
        aria-atomic="false"
        className="pointer-events-none fixed end-0 start-0 top-0 z-50 flex flex-col items-center gap-2 p-4"
      >
        {toasts.map((entry) => (
          <button
            key={entry.id}
            type="button"
            onClick={() => dismiss(entry.id)}
            className={cn(
              'pointer-events-auto flex max-w-[24rem] items-start gap-3 rounded-md border p-4 text-start text-sm text-ink shadow-elev-3',
              INTENT_CLASS[entry.intent],
            )}
          >
            <span aria-hidden="true" className="select-none pt-[2px] leading-none">
              {INTENT_GLYPH[entry.intent]}
            </span>
            <span className="min-w-0 flex-1">{entry.message}</span>
          </button>
        ))}
      </div>
    </ToastContext.Provider>
  );
}

export function useToast(): ToastValue {
  const context = useContext(ToastContext);
  if (!context) throw new Error('useToast must be used inside <ToastProvider>.');
  return context;
}
